import {
  DEFAULT_KEYBOARD_BINDINGS,
  type GameAction,
  type KeyboardBindings,
} from '../input/input-contract';
import { LOCAL_STATE, type LocalSettings } from '../persistence/local-state';

export const keyboardBindingsForScheme = (
  scheme: LocalSettings['controlScheme'],
): KeyboardBindings => {
  if (scheme === 'wasd') return DEFAULT_KEYBOARD_BINDINGS;
  return {
    ...DEFAULT_KEYBOARD_BINDINGS,
    moveUp: ['KeyI', 'ArrowUp'],
    moveDown: ['KeyK', 'ArrowDown'],
    moveLeft: ['KeyJ', 'ArrowLeft'],
    moveRight: ['KeyL', 'ArrowRight'],
  };
};

export const applyAudioAction = (
  settings: LocalSettings,
  action: Extract<GameAction, 'mute' | 'volume-up' | 'volume-down'>,
): LocalSettings => {
  const masterVolume =
    action === 'volume-up'
      ? Math.min(100, settings.audio.masterVolume + 10)
      : action === 'volume-down'
        ? Math.max(0, settings.audio.masterVolume - 10)
        : settings.audio.masterVolume;
  return {
    ...settings,
    audio: {
      ...settings.audio,
      muted: action === 'mute' ? !settings.audio.muted : settings.audio.muted,
      masterVolume,
    },
  };
};

interface SettingsStore {
  loadSettings(): LocalSettings;
  saveSettings(settings: LocalSettings): LocalSettings;
}

export interface SettingsController {
  load(): LocalSettings;
  save(settings: LocalSettings): LocalSettings;
  applyAudioAction(
    action: Extract<GameAction, 'mute' | 'volume-up' | 'volume-down'>,
  ): LocalSettings;
  subscribe(listener: (settings: LocalSettings) => void): () => void;
}

export const createSettingsController = (store: SettingsStore): SettingsController => {
  const listeners = new Set<(settings: LocalSettings) => void>();
  const publish = (settings: LocalSettings): LocalSettings => {
    for (const listener of listeners) listener(settings);
    return settings;
  };
  return {
    load: () => store.loadSettings(),
    save: (settings) => publish(store.saveSettings(settings)),
    applyAudioAction: (action) =>
      publish(store.saveSettings(applyAudioAction(store.loadSettings(), action))),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

export const SETTINGS = createSettingsController(LOCAL_STATE);
